// Права сотрудников — общий список для админки (матрица, кнопки) и сервера
// (server/utils/access.ts). ADMIN может всё и списком не пользуется;
// у MANAGER набор хранится в users.permissions и задаётся в «Пользователях».
//
// danger — действие с деньгами, необратимое или меняющее работу всего
// магазина: в матрице помечено, новому менеджеру по умолчанию не выдаётся.
export const PERMISSION_GROUPS = [
  {
    key: "products",
    label: "Товары",
    items: [
      { key: "products.create", label: "Создание товаров" },
      {
        key: "products.edit",
        label: "Редактирование карточки",
        hint: "Название, описание, категория, размеры, упаковка, ссылки на маркетплейсы, похожие товары",
      },
      { key: "products.images", label: "Фотографии", hint: "Загрузка, удаление и порядок фото" },
      { key: "products.price", label: "Изменение цен" },
      { key: "products.stock", label: "Остатки на складе" },
      { key: "products.visibility", label: "Скрытие и показ товаров на сайте" },
      { key: "products.sort", label: "Сортировка выдачи" },
      {
        key: "products.delete",
        label: "Удаление карточки товара",
        hint: "Удаляет товар вместе с фото и связями — вернуть нельзя",
        danger: true,
      },
    ],
  },
  {
    key: "catalog",
    label: "Каталог",
    items: [
      { key: "seasons.manage", label: "Сезонные подборки" },
      { key: "sections.manage", label: "Разделы каталога", hint: "Картинки разделов и тексты для поисковиков" },
    ],
  },
  {
    key: "orders",
    label: "Заказы",
    items: [
      {
        key: "orders.view",
        label: "Просмотр заказов",
        hint: "Список заказов с контактами покупателей (персональные данные)",
      },
      {
        key: "orders.status",
        label: "Смена статуса заказа",
        hint: "И письмо покупателю о новом статусе; оформление и отмена отправления в СДЭК",
      },
      {
        key: "orders.compensation",
        label: "Компенсация промокодом",
        hint: "Персональный промокод вместо возврата — в пределах лимита из настроек магазина",
      },
    ],
  },
  {
    key: "returns",
    label: "Возвраты",
    items: [
      {
        key: "returns.goods",
        label: "Возврат товара",
        hint: "Оформить, одобрить, отказать, указать трек-номер, принять товар",
      },
      {
        key: "returns.cdek",
        label: "Оформление возврата в СДЭК (платно)",
        hint: "Клиентский возврат СДЭК: покупатель сдаёт посылку в пункт, СДЭК выставит нам счёт за доставку",
        danger: true,
      },
      {
        key: "refunds.money",
        label: "Возврат денег покупателю",
        hint: "Отправка возврата в CDEK Pay — деньги уходят с расчётного счёта",
        danger: true,
      },
    ],
  },
  {
    key: "marketing",
    label: "Маркетинг и финансы",
    items: [
      { key: "promo.manage", label: "Промокоды", hint: "Создание, изменение и удаление скидок", danger: true },
      { key: "sales.view", label: "Продажи", hint: "Выручка, средний чек, динамика", danger: true },
    ],
  },
  {
    key: "settings",
    label: "Настройки",
    items: [
      {
        key: "settings.manage",
        label: "Настройки магазина",
        hint: "СДЭК, приём оплаты, почта, видимость цен и корзины",
        danger: true,
      },
    ],
  },
];

export const PERMISSIONS = PERMISSION_GROUPS.flatMap((group) =>
  group.items.map((item) => ({ ...item, group: group.key }))
);

export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);

export const PERMISSION_LABEL = Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.label]));

// Новому менеджеру — всё, кроме опасного; дальше администратор правит в матрице
export const DEFAULT_MANAGER_PERMISSIONS = PERMISSIONS.filter((p) => !p.danger).map((p) => p.key);

export const PERMISSION_PRESETS = [
  { key: "safe", label: "Всё, кроме опасного", permissions: DEFAULT_MANAGER_PERMISSIONS },
  {
    key: "content",
    label: "Контент-менеджер",
    permissions: [
      "products.create",
      "products.edit",
      "products.images",
      "products.stock",
      "products.visibility",
      "products.sort",
      "seasons.manage",
      "sections.manage",
    ],
  },
  {
    key: "orders",
    label: "Менеджер заказов",
    permissions: ["orders.view", "orders.status", "returns.goods", "products.stock"],
  },
  { key: "all", label: "Всё", permissions: PERMISSION_KEYS },
  { key: "none", label: "Ничего", permissions: [] },
];

// Оставляет только известные ключи: чтобы в базу не попал мусор из запроса
export const normalizePermissions = (list) =>
  PERMISSION_KEYS.filter((key) => Array.isArray(list) && list.includes(key));

// Роли одинаковы на клиенте и сервере
export const isAdminUser = (user) => !!user && !user.blocked_at && !!user.roles?.includes("ADMIN");
export const isManagerUser = (user) =>
  !!user && !user.blocked_at && !user.roles?.includes("ADMIN") && !!user.roles?.includes("MANAGER");
export const isStaffUser = (user) => isAdminUser(user) || isManagerUser(user);

// Может ли человек выполнить действие. Список — «любое из»
export const userCan = (user, permission) => {
  if (isAdminUser(user)) return true;
  if (!isManagerUser(user)) return false;
  const list = Array.isArray(permission) ? permission : [permission];
  return list.some((key) => user.permissions?.includes(key));
};
